'use client';

import Link from 'next/link';
import { MapPinIcon, ArrowRightIcon } from '@heroicons/react/24/solid';

import { useLocalityService } from '@/providers/Locality';
import { cleanName } from '@/services/clean-string-service';
import { LocalityModel } from '@/types/LocalityModel';

const NearbyLocalites: React.FC<{ nearby: LocalityModel[] }> = ({ nearby }) => {
  const localityService = useLocalityService();

  if (!nearby.length) return null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
          Aussi disponible près de {localityService.getLocaliteName()}
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          Nos équipes interviennent aussi dans ces communes voisines.
        </p>
      </div>
      <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {nearby.map((localite) => {
          const href = localityService.changeLocaliteInUrl(
            `${cleanName(localite.displayName)}-${localite.id}`,
          );

          if (!href) return null;

          const postalCode = localite.postalCodes?.[0];
          const showCommune =
            localite.commune && localite.commune.toLowerCase() !== localite.displayName.toLowerCase();

          return (
            <li key={localite.id}>
              <Link
                href={href}
                className="group flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors hover:border-[#009EE2] hover:shadow-md"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#009EE2]/10 text-[#009EE2]">
                  <MapPinIcon className="size-5" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-gray-900 group-hover:text-[#009EE2]">
                    {localite.displayName}
                  </span>
                  {(showCommune || postalCode) && (
                    <span className="block truncate text-sm text-gray-500">
                      {[postalCode, showCommune ? localite.commune : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  )}
                </span>
                <ArrowRightIcon
                  className="size-4 shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-[#009EE2]"
                  aria-hidden="true"
                />
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export { NearbyLocalites };
